import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { commonErrorMessages } from '../../messages';
import { DossierFilter, WalletSections } from '../../model';
import { ApiRequestStatus } from '../../model/api-request';
import { ErrorMessages } from '../../model/error';
import dossierRepository from '../../repositories/dossier/dossier-repository';
import { addAllBasicExtraReducers } from '../../utils/action-builder-utils';
import { AsyncThunkConfig } from '../async-thunk-config';
import { RootState } from '../root-store';

export interface DossierState {
  dossierFilter: DossierFilter;
  dossierFilterRequestStatus: ApiRequestStatus;
  dossierFilterError?: DossierError;
  defaultDossier: DossierFilter;
}

const initialState: DossierState = {
  dossierFilter: null,
  dossierFilterRequestStatus: 'idle',
  defaultDossier: null,
};

const dossierSlice = createSlice({
  name: 'data/dossier',
  initialState,
  reducers: {
    setDossierFilter(state, action: PayloadAction<DossierFilter>) {
      state.dossierFilter = action.payload;
    },
    clearDossierError(state) {
      state.dossierFilterError = undefined;
      state.dossierFilterRequestStatus = 'idle';
    },
    resetFilter: (state) => {
      const dossier = state.dossierFilter?.dossier.map((item) => ({
        ...item,
        flagSelected: true,
      }));
      state.dossierFilter = dossier ? { dossier } : null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    addAllBasicExtraReducers(builder, retrieveDossier, 'defaultDossier');
  },
});

export const dossierSliceActions = dossierSlice.actions;

export default dossierSlice.reducer;

export type DossierError = 'GENERIC_ERROR';

export const retrieveDossier = createAsyncThunk<
  DossierFilter,
  void,
  AsyncThunkConfig<DossierError>
>('retrieve-dossier', async (_, thunkApi) => {
  try {
    return await dossierRepository.getDossier(thunkApi);
  } catch (error) {
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const selectDossierState = (state: RootState): DossierState =>
  state.data?.dossier;

export const getDossierFilter = (
  dossierFilter: DossierFilter,
  section: WalletSections
) => {
  return (
    dossierFilter &&
    ({
      dossier: dossierFilter.dossier?.filter(
        (item) =>
          (section === 'MY_WALLET' && item.flagIsOwner) ||
          (section === 'SHARED_ACCOUNTS' && !item.flagIsOwner)
      ),
    } as DossierFilter)
  );
};

export const messagesForDossierError = (error: DossierError): ErrorMessages => {
  switch (error) {
    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};
